import { createSelector, createStructuredSelector } from "reselect"
import { getPacPaths } from "imports/desktopPaths"
import { fromJS } from "immutable"
import { createEarlyUndefinedSelector, createToJsSelector } from "shared/imports/sharedHelperFunctions"
import {
    selectHasMultipleLedgerSettings,
    selectIsLoadingLedgerSettings,
    selectLedgerAccounts,
    selectLedgerSettingsId,
    selectLedgerSettingsList,
    selectLedgerSettingsName,
} from "shared/pac/selectors"
import { centsToCurrencyStr } from "shared/pac/helperFunctions"
import { PRINT_CHECKS_FORM_KEY } from "shared/pac/constants"
const { LedgerAccountType } = DjangIO.app.ledger.types

export const selectPacListViewPaintings = createSelector(selectLedgerSettingsList, (ledgerSettingsList) => {
    if (ledgerSettingsList) {
        const paintingsList = ledgerSettingsList.map((ledgerSettings, i) => {
            const id = ledgerSettings.get("id")
            const name = ledgerSettings.get("name")
            const pacronym = ledgerSettings.get("pacronym")
            return {
                segueLink: getPacPaths({ ledgerSettingsId: id })._pacDetailHome,
                permissions: [],
                heading: `${name} (${pacronym})`,
                icon: "usd",
                iconFamily: "fad",
                dataCy: `pac-${name}`,
                key: `pac-${pacronym}-${i}`,
                order: i,
            }
        })

        return paintingsList.toJS()
    }

    return []
})

export const selectLedgerSettingsHomePath = createSelector(
    selectLedgerSettingsId,
    (ledgerSettingsId) => getPacPaths({ ledgerSettingsId })._pacDetailHome,
)

export const selectTransactionListPath = createSelector(
    selectLedgerSettingsId,
    (ledgerSettingsId) => getPacPaths({ ledgerSettingsId })._pacTransactionsList,
)

export const selectNewReceiptPath = createSelector(
    selectLedgerSettingsId,
    (ledgerSettingsId) => getPacPaths({ ledgerSettingsId })._pacReceiptNew,
)

export const selectNewDisbursementPath = createSelector(
    selectLedgerSettingsId,
    (ledgerSettingsId) => getPacPaths({ ledgerSettingsId })._pacDisbursementNew,
)

export const selectPacDetailViewPaintings = createSelector(selectLedgerSettingsId, (ledgerSettingsId) => [
    {
        segueLink: getPacPaths({ ledgerSettingsId })._pacTransactionsList,
        permissions: [],
        content: "Receipts and Disbursements",
        heading: "PAC Transactions",
        icon: "hand-holding-usd",
        iconFamily: "fad",
        dataCy: "painting-transactions",
        order: 1,
    },
    {
        segueLink: getPacPaths({ ledgerSettingsId })._pacFinancials,
        permissions: [],
        content: "Manage and Reconcile Bank Accounts",
        heading: "Financials",
        icon: "piggy-bank",
        iconFamily: "fad",
        dataCy: "painting-financials",
        order: 2,
    },
    {
        segueLink: getPacPaths({ ledgerSettingsId })._pacFilingsList,
        permissions: [],
        content: "Prepare and File FEC Reports",
        heading: "Filings",
        icon: "file-invoice-dollar",
        iconFamily: "fad",
        dataCy: "painting-filings",
        order: 3,
    },
    {
        segueLink: getPacPaths({ ledgerSettingsId })._pacSettings,
        permissions: [],
        content: "Update Your Form 1 and 1M Information",
        heading: "Settings",
        icon: "cog",
        iconFamily: "fad",
        dataCy: "painting-settings",
        order: 4,
    },
])

export const selectFecFilingFrenquencyChoices = () =>
    DjangIO.app.pac.types.PacFileFormPeriodType.items()
        .filter((item) => item.show_in_pac_settings_form)
        .sort(DjangIO.valueSort)

export const selectAccountTypeChoices = () => [LedgerAccountType.checking_account, LedgerAccountType.savings_account]

export const selectShouldTransactionFormCreateNewAfterSubmission = (state) =>
    state.pac.getIn(["transactionForm", "isContinuingToMakeNewTransaction"])

export const selectCheckForm = (state) => state.pac.get("checkForm")

export const selectIsCheckFormModalOpen = (state) => state.pac.getIn(["checkForm", "isModalOpen"])

export const selectCheckFormModalOpen = createSelector(selectCheckForm, (checkForm) => checkForm.get("modalOpen"))

export const selectCheckFormSingleCheckId = createSelector(selectCheckForm, (checkForm) =>
    checkForm.get("singleCheckId"),
)

export const selectDefaultReferenceNumber = createSelector(selectCheckForm, (checkForm) =>
    checkForm.get("defaultReferenceNumber"),
)

export const selectPrintChecksSelectedBankAccount = (state) => {
    const bankAccount = state.form.getIn([PRINT_CHECKS_FORM_KEY, "values", "bank_account"])
    const immutableBankAccount = fromJS(bankAccount)
    return immutableBankAccount && immutableBankAccount.get("value")
}

export const selectPrintChecksSelectedBankAccountBalance = createSelector(
    selectPrintChecksSelectedBankAccount,
    selectLedgerAccounts,
    (bankAccountId, bankAccounts) => {
        const relevantAccount = bankAccounts.toJS().filter((account) => account.id === bankAccountId)
        const account = relevantAccount.length && relevantAccount[0]
        if (!account) {
            return
        }
        return centsToCurrencyStr(parseInt(account.current_balance))
    },
)

// TODO: Fetch LedgerSettings, set initial values from object
export const selectPacSettingsInitialValues = () => ({
    bankAccounts: [],
})

export const pacListViewConnection = createStructuredSelector({
    hasMoreThanOnePac: selectHasMultipleLedgerSettings,
    isLoading: selectIsLoadingLedgerSettings,
    paintings: selectPacListViewPaintings,
})

export const pacDetailViewConnection = createStructuredSelector({
    isLoading: selectIsLoadingLedgerSettings,
    pacName: selectLedgerSettingsName,
    paintings: selectPacDetailViewPaintings,
})

export const pacSettingsFormConnection = createStructuredSelector({
    accountTypeChoices: selectAccountTypeChoices,
    fecFilingFrenquencyChoices: selectFecFilingFrenquencyChoices,
    initialValues: selectPacSettingsInitialValues,
    ledgerSettingsHomePath: selectLedgerSettingsHomePath,
})

export const selectBankAccountListViewPaintings = createEarlyUndefinedSelector(
    selectLedgerSettingsId,
    selectLedgerAccounts,
    (ledgerSettingsId, ledgerAccounts) =>
        ledgerAccounts
            .filter((account) =>
                [LedgerAccountType.checking_account.value, LedgerAccountType.savings_account.value].includes(
                    account.get("account_type"),
                ),
            )
            .map((account, i) => ({
                segueLink: getPacPaths({ ledgerSettingsId, accountId: account.get("id") })._pacSettingsFinancials,
                permissions: [],
                heading: account.get("nickname") || account.get("name"),
                dataCy: `bank-account-${i}`,
                order: i,
                balance: centsToCurrencyStr(
                    account.get("current_balance") == 0 && account.get("initial_bank_balance") // If current_balance is zero, use initial_bank_balance if set.
                        ? account.get("initial_bank_balance")
                        : account.get("current_balance"),
                ),
            })),
)

export const selectIsLoadingBankAccountListView = createSelector(
    selectBankAccountListViewPaintings,
    (paintings) => !paintings,
)

export const selectBankAccountListViewPaintingsJs = createToJsSelector(selectBankAccountListViewPaintings)

export const selectAddBankAccountPath = createSelector(
    selectLedgerSettingsId,
    (ledgerSettingsId) =>
        getPacPaths({
            ledgerSettingsId,
            accountId: "new",
        })._pacSettingsFinancials,
)

export const selectFinancialsPath = createSelector(
    selectLedgerSettingsId,
    (ledgerSettingsId) => getPacPaths({ ledgerSettingsId })._pacFinancials,
)

export const pacBankAccountListViewConnection = createStructuredSelector({
    isLoading: selectIsLoadingBankAccountListView,
    paintings: selectBankAccountListViewPaintingsJs,
    addBankAccountPath: selectAddBankAccountPath,
    financialsPath: selectFinancialsPath,
})
